import { useNavigate } from "react-router-dom";
import { Stethoscope, Microscope, User, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Extras";

const NODES = [
  [Stethoscope, "Clinician", "#2412B5", { left: "17%", top: "72.5%" }, "0s"],
  [Microscope, "Diagnostician", "#086477", { left: "83%", top: "72.5%" }, "1.4s"],
  [User, "You", "#086B2D", { left: "50%", top: "16.25%" }, "2.8s"],
];

function Constellation() {
  return (
    <div className="relative mx-auto aspect-[5/4] w-full max-w-sm" data-testid="joint-promo-visual">
      <div className="jp-orbit absolute inset-[12%] rounded-full" />
      <div className="absolute inset-[24%] rounded-full border border-[#2412B5]/10" />
      <svg viewBox="0 0 100 80" className="absolute inset-0 h-full w-full" aria-hidden="true">
        <defs><linearGradient id="jp-g" x1="0" x2="1"><stop offset="0" stopColor="#2412B5" /><stop offset="0.5" stopColor="#086B2D" /><stop offset="1" stopColor="#086477" /></linearGradient></defs>
        <path d="M17 58 L50 13 L83 58 Z" fill="none" stroke="#2412B5" strokeOpacity="0.08" strokeWidth="0.6" />
        <path d="M17 58 L50 13 L83 58 Z" fill="none" stroke="url(#jp-g)" strokeWidth="0.9" strokeLinecap="round" className="jp-flow" />
        <circle r="1.3" fill="#FF9700" className="jp-dot"><animateMotion dur="6s" repeatCount="indefinite" path="M17 58 L50 13 L83 58 Z" calcMode="spline" keyTimes="0;1" keySplines="0.45 0 0.55 1" /></circle>
      </svg>
      <div className="absolute left-1/2 top-[53.75%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
        <span className="jp-core h-12 w-12 rounded-full bg-white border border-[#E2E8ED] flex items-center justify-center shadow-[0_10px_30px_-10px_rgba(36,18,181,0.45)]"><img src="/icon-192.png" alt="" className="h-7 w-7" /></span>
        <span className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#2412B5]/70">One call</span>
      </div>
      {NODES.map(([I, l, c, pos, d]) => (
        <div key={l} className="absolute -translate-x-1/2 -translate-y-6 flex flex-col items-center" style={pos} data-testid={`joint-promo-node-${l.toLowerCase()}`}>
          <span className="jp-node relative h-12 w-12 rounded-2xl flex items-center justify-center bg-white border shadow-sm" style={{ borderColor: `${c}33`, color: c, animationDelay: d }}>
            <span className="jp-halo absolute inset-0 rounded-2xl" style={{ animationDelay: d }} /><I className="h-5 w-5" />
          </span>
          <span className="mt-1.5 text-xs font-medium text-[#17232E]/80">{l}</span>
        </div>))}
    </div>
  );
}

export function JointPromo() {
  const nav = useNavigate();
  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16" data-testid="joint-promo-section">
      <Reveal>
        <div className="surface relative overflow-hidden rounded-3xl p-7 sm:p-10 grid lg:grid-cols-2 gap-8 items-center">
          <div className="jp-sheen absolute inset-0 pointer-events-none" />
          <div className="relative min-w-0">
            <p className="eyebrow">New · Diagnosticians on Nirogyam</p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold leading-tight">Two specialists. One call. <span className="text-[#2412B5]">Your reports, fully understood.</span></h2>
            <p className="mt-4 text-sm sm:text-base muted max-w-lg">Talk to a pathologist, microbiologist, biochemist or radiologist who explains your reports. Or book a joint consultation: your clinician and a diagnostician join one video call to agree on your care plan.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button className="rounded-full" onClick={() => nav("/patient/joint-consult")} data-testid="joint-promo-joint-btn">Book a joint consult <ArrowRight className="h-4 w-4 ml-2" /></Button>
              <Button variant="outline" className="rounded-full" onClick={() => nav("/patient/report-consult")} data-testid="joint-promo-report-btn">Get reports explained</Button>
            </div>
          </div>
          <div className="relative"><Constellation /></div>
        </div>
      </Reveal>
    </section>
  );
}
